import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../hooks/useAuth';

interface ChatSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const ChatSocketContext = createContext<ChatSocketContextType>({
  socket: null,
  isConnected: false,
});

export const ChatSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token || !user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    const apiUrl = (import.meta.env.VITE_API_URL || 'http://localhost:2000/api/v1').replace(/\/$/, '');
    const baseUrl = apiUrl.split('/api/v1')[0];

    const socket = io(`${baseUrl}/direct-chat`, {
      auth: { token },
      path: '/socket.io',
      transports: ['polling'],
      withCredentials: true,
      reconnectionAttempts: 5,
      timeout: 10000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      console.log('ChatSocketContext: Connected to direct-chat');
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      console.log('ChatSocketContext: Disconnected');
    });

    return () => {
      if (socket) {
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [token, user]);

  const value = React.useMemo(() => ({ 
    socket: socketRef.current, 
    isConnected 
  }), [isConnected]);

  return (
    <ChatSocketContext.Provider value={value}>
      {children}
    </ChatSocketContext.Provider>
  );
};

export const useChatSocket = () => useContext(ChatSocketContext);
